import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Grace_ListResponseDados } from '../contracts/Grace_ListResponse'
import type { Grace_PeriodRow } from '../contracts/Grace_PeriodRow'
import type { Grace_GrantInput } from '../contracts/Grace_GrantInput'
import type { Grace_RevokeInput } from '../contracts/Grace_RevokeInput'
import type { Subscriptions_ExtendResponseDados } from '../contracts/Subscriptions_ExtendResponse'
// Import side-effects: registers the contracts with the responseContract registry.
import '../contracts/Grace_ListResponse'
import '../contracts/Grace_PeriodRow'
import '../contracts/Grace_GrantInput'
import '../contracts/Grace_RevokeInput'
import '../contracts/Subscriptions_ExtendResponse'

/**
 * useGraceAdmin — SOLE network owner for subscriptions/grace/* admin routes.
 *
 * All requests go to `config.api.sistemaHost` (app-api-service).
 * The `no_httpservice_in_vue` qa rule forbids any fetch in .vue files —
 * this composable is the single gateway.
 *
 * Call `_reset()` in afterEach for test isolation.
 */

// ── Module-scope singleton state ────────────────────────────────────────────

const graceRows = ref<Grace_PeriodRow[]>([])

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useGraceAdmin() {
  /**
   * listGrace — GET subscriptions/grace/list.
   * Populates the module-scope graceRows ref.
   */
  async function listGrace(): Promise<void> {
    try {
      const raw = await httpService.get(
        sistemaUrl('subscriptions/grace/list'),
        { responseContract: 'Grace_ListResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: Grace_ListResponseDados }
      graceRows.value = resp.dados.rows
      log('useGraceAdmin', 'listGrace', 'Períodos carregados', { count: graceRows.value.length })
    } catch (err) {
      log('useGraceAdmin', 'listGrace', 'Erro ao carregar períodos de carência', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * grant — POST subscriptions/grace/grant.
   * Binds EXACTLY the Grace_GrantInput field names.
   */
  async function grant(input: Grace_GrantInput): Promise<Subscriptions_ExtendResponseDados> {
    const body: Grace_GrantInput = {
      franquia_id: input.franquia_id,
      days: input.days,
      plan_id: input.plan_id,
      ...(input.reason != null ? { reason: input.reason } : {}),
    }
    // throwOnEnvelopeFailure: o backend responde HTTP 200 mesmo na RECUSA
    // (sendError/sendResponse não mexem no status), então o httpService não
    // lançaria sozinho e a validação de contrato não olha `dados` quando o
    // envelope diz falha. Sem esta opção a tela celebra uma concessão que não
    // aconteceu — a mesma classe de defeito que o S9 encontrou no "Estender
    // carência" e que já chegou quatro vezes. Ver skill billing-staff-console §4.
    const raw = await httpService.post(
      sistemaUrl('subscriptions/grace/grant'),
      body,
      {
        inputContract: 'Grace_GrantInput',
        responseContract: 'Subscriptions_ExtendResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'grace_grant_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_ExtendResponseDados }

    log('useGraceAdmin', 'grant', 'Carência concedida', {
      franquia_id: input.franquia_id,
      days: input.days,
      plan_id: input.plan_id,
    })
    return resp.dados
  }

  /**
   * revoke — POST subscriptions/grace/revoke.
   * Binds EXACTLY the Grace_RevokeInput field names.
   */
  async function revoke(input: Grace_RevokeInput): Promise<Subscriptions_ExtendResponseDados> {
    const body: Grace_RevokeInput = {
      grace_id: input.grace_id,
      ...(input.reason != null ? { reason: input.reason } : {}),
    }
    // Mesma regra de envelope do grant() — uma revogação recusada NÃO pode
    // chegar ao modal como "Carência revogada com sucesso".
    const raw = await httpService.post(
      sistemaUrl('subscriptions/grace/revoke'),
      body,
      {
        inputContract: 'Grace_RevokeInput',
        responseContract: 'Subscriptions_ExtendResponse',
        skipErrorDisplay: false,
        throwOnEnvelopeFailure: 'grace_revoke_failed',
      },
    )
    const resp = raw as { dados: Subscriptions_ExtendResponseDados }
    log('useGraceAdmin', 'revoke', 'Carência revogada', { grace_id: input.grace_id })
    return resp.dados
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    graceRows.value = []
  }

  return {
    // reactive state
    graceRows,
    // methods
    listGrace,
    grant,
    revoke,
    _reset,
  }
}
